import { useCallback, useMemo, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Container, Header } from "components/Page"
import { Route, Switch, useParams } from "react-router-dom"
import { css, cx } from "@emotion/css"
import { fetchAgencyClientProfile, fetchClientConfigProfile } from "api/clients"
import {
  fetchAgencyClients,
  updateImportSettings,
  updateTeamLeadImportSettings,
} from "api/agencies"
import { queryCache, useMutation, useQuery } from "react-query"
import { styledTabClasses, styledTabsProps } from "components/Tabs"

import AgencyClientCopies from "./AgencyClientCopies"
import { AgencyClientDocuments } from "./AgencyClientDocuments"
import AgencyClientFields from "./AgencyClientFields"
import AgencyClientExchangeRates from "./AgencyClientExchangeRates"
import { AgencyClientFlow } from "./AgencyClientFlow"
import AgencyClientMediaTypes from "./AgencyClientMediaTypes"
import { AgencyClientProducts } from "./AgencyClientProducts"
import { AgencyClientProfile } from "./AgencyClientProfile"
import AgencyClientTargets from "./AgencyClientTargets"
import { AgencyClientTaxonomy } from "./AgencyClientTaxonomy"
import { AgencyClientTeams } from "./AgencyClientTeams"
import { AuthorizedRoute } from "components/AuthorizedRoute"
import Badge from "components/Badge"
import Breadcrumbs from "components/Breadcrumbs"
import { ColorMap } from "utils/color"
import Commissions from "../../Commissions"
import { ConfigCLientTeams } from "./ConfigClientTeams"
import Fees from "../../Fees"
import ImportModal from "components/Modals/ClientImportModal"
import { MbaConfiguration } from "../../MbaConfiguration"
import MoreActions from "components/MoreActions"
import Surface from "components/Surface"
import { TabLink } from "components/Link"
import { Tabs } from "@material-ui/core"
import { useAgencyId } from "hooks/useAgencyId"
import { useAuthenticated } from "providers/A12n"
import { useFeedback } from "components/FeedbackSnackbar"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"
import { CampaignMbaConfig } from "routes/Base/Campaigns/Details/MbaConfig"

const tabs = [
  "profile" /* t(agency.client.profile) */,
  "products" /* t(agency.client.products) */,
  "external-ids" /* t("agency.client.external-ids") */,
  "teams" /* t(agency.client.teams) */,
  "flow" /* t(agency.client.flow) */,
  "media-types" /* t(agency.client.media-types) */,
  "fields" /* t(agency.client.fields) */,
  "targets" /* t(agency.client.targets) */,
  "copies" /* t(agency.client.copies)*/,
  "costs" /* t(agency.client.costs) */,
  "exchange_rates" /* t(agency.client.exchange_rates) */,
  "commissions" /* t(agency.client.commissions) */,
  // "templates" /* t(agency.client.templates) */,
  "documents" /* t(agency.client.documents) */,
  "mba-configuration" /* t(agency.client.mba) */,
  "campaign-mba-configuration" /* t(agency.client.campaign.mba) */,
]

export default function AgencyClientDetails() {
  const { t } = useI18n()
  const { meta } = useAuthenticated()
  const isTeamLead = meta?.teamLead
  const isAgencyFinance = meta?.userRole === "AGENCY_FINANCE"
  const isAgencyAdmin = meta?.userRole === "AGENCY_ADMINISTRATOR"
  const { hasAuthority } = useAuthenticated()
  const sendFeedback = useFeedback((s) => s.send)
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const [importModalOpen, setImportModalOpen] = useState(false)
  const canViewDocuments = hasAuthority(["AUTH_VIEW_CLIENT_DOCUMENT"])
  const [selectedClientId, setSelectedClientId] = useState<string>()
  const [selectedAgencyId, setSelectedAgencyId] = useState<string>()
  const search = useLocationSearch()
  const {
    id,
    tab = "profile",
    configId,
  } = useParams<{ id: string; tab: string; configId: string }>()

  const showMoreActions =
    tab === "fields" && hasAuthority(["AUTH_COPY_AGENCY_CLIENT_CONFIG"])

  const params = new URLSearchParams({
    excludeClientId: id,
    limit: "100",
    offset: "0",
  })

  isTeamLead && params.set("admin", "true")

  const TeamsComponent = isTeamLead ? ConfigCLientTeams : AgencyClientTeams

  const { agencyId } = useAgencyId()

  const queryFn = isTeamLead
    ? fetchClientConfigProfile
    : fetchAgencyClientProfile
  const queryId = isTeamLead ? configId : id

  const { data: client } = useQuery(
    ["agency-client", queryId, agencyId],
    queryFn,
    {
      enabled: isTeamLead ? !!id : !!id && !!agencyId,
    },
  )

  const _agencyId = isTeamLead ? client?.agencyId : agencyId

  const { data: agencyClients } = useQuery(
    ["agency-clients", _agencyId, params.toString()],
    fetchAgencyClients,
    { enabled: !!agencyId || !!client?.agencyId },
  )

  const description1 = t("import.settings.client_group.description1")
  const description2 = t("import.settings.client_group.description2")

  const clients = agencyClients

  const groupName = client?.groupName

  const invalidateQueries = [
    "media-config",
    "global-media-fields",
    "global-media-types",
    "agency-clients",
    `mba-config`,
  ]

  const mutateFn = !isTeamLead
    ? updateImportSettings
    : updateTeamLeadImportSettings

  const [importSettings] = useMutation(mutateFn, {
    onSuccess: () => {
      setModalInfo(undefined)
      setImportModalOpen(false)
      invalidateQueries.map((query) => queryCache.invalidateQueries(query))
    },
    onError: (e) => {
      sendFeedback(t(e.message), "error")
    },
  })

  const importParams = useMemo(() => {
    return {
      agencyId: !isTeamLead ? agencyId : undefined,
      clientAgencyConfigId: isTeamLead ? configId : undefined,
      targetClientId: id,
      copyFromClientGroup: true,
    }
  }, [agencyId, configId, id, isTeamLead])

  const onClientGroup = useCallback(() => {
    setModalInfo({
      title: t("import.settings.client_group.title"),
      type: "primary",
      description: description1 + groupName + description2,
      submitLabel: t("import.settings.client_group.action"),
      onSubmit: async () => await importSettings(importParams),
    })
  }, [importSettings, t, importParams, description1, description2, groupName])

  const handleSubmit = useCallback(async () => {
    !!selectedAgencyId &&
      (await importSettings({
        agencyId: selectedAgencyId,
        targetClientId: id,
        clientAgencyConfigId: isTeamLead ? configId : undefined,
        clientId: selectedClientId,
      }))
    setSelectedClientId(undefined)
    setSelectedAgencyId(undefined)
  }, [
    selectedAgencyId,
    configId,
    isTeamLead,
    selectedClientId,
    id,
    importSettings,
  ])

  const handleClose = () => {
    setSelectedClientId(undefined)
    setSelectedAgencyId(undefined)
    setImportModalOpen(false)
  }

  const activeTabs = !canViewDocuments
    ? tabs.filter((tb) => tb !== "documents")
    : tabs

  const isModalOpen = Boolean(modalInfo)

  return (
    <Container>
      <div className={styles.row}>
        <div className={styles.column}>
          <Breadcrumbs
            current={client?.localName}
            items={[
              {
                backLink: `${t("nav.admin")} / ${t("nav.agency.clients")}`,
                to: "/agency-clients",
              },
            ]}
          />
          <Header
            label={client?.localName}
            badge={
              <Badge color={ColorMap[client?.configStatus as any]}>
                {client?.configStatus}
              </Badge>
            }
          />
        </div>
        {showMoreActions && (
          <div className={styles.actions}>
            <MoreActions
              options={
                groupName === undefined
                  ? [
                      {
                        label: t("import.settings.client"),
                        onClick: () => {
                          setImportModalOpen(true)
                        },
                      },
                    ]
                  : [
                      {
                        label: t("import.settings.client"),
                        onClick: () => {
                          setImportModalOpen(true)
                        },
                      },
                      {
                        label: t("import.settings.client_group"),
                        onClick: () => {
                          onClientGroup()
                        },
                      },
                    ]
              }
            />
          </div>
        )}
      </div>
      <Surface flexDirection="row" className={styles.wrapper}>
        <Tabs
          value={tab}
          {...styledTabsProps}
          classes={{
            ...styledTabsProps.classes,
            flexContainer: cx(
              styledTabsProps?.classes?.flexContainer,
              styles.tabsFlexContainer,
            ),
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {activeTabs.map((tb) => (
            <TabLink
              key={tb}
              to={{
                pathname: `/agency-clients/${id}/${tb}/${configId}`,
                search: search.search,
              }}
              classes={styledTabClasses}
              value={tb}
              label={t(`agency.client.${tb}`)}
            />
          ))}
        </Tabs>
        <div>
          <Switch>
            <AuthorizedRoute
              allowed={["AUTH_VIEW_AGENCY_CLIENT_PROFILE"]}
              component={AgencyClientProfile}
              path="/agency-clients/:id/profile/:configId"
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_PRODUCTS", "AUTH_VIEW_CLIENT"]}
              component={AgencyClientProducts}
              path="/agency-clients/:id/products/:configId"
            />
            <AuthorizedRoute
              allowed={[
                "AUTH_VIEW_CLIENT_TAXONOMY",
                "AUTH_MANAGE_CLIENT_TAXONOMY",
              ]}
              component={AgencyClientTaxonomy}
              path="/agency-clients/:id/external-ids/:configId"
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_AGENCY_CLIENT_TEAM"]}
              component={TeamsComponent}
              path="/agency-clients/:id/teams/:configId"
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_FLOW"]}
              component={AgencyClientFlow}
              path="/agency-clients/:id/flow/:configId"
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_MEDIA_TYPE"]}
              component={AgencyClientMediaTypes}
              path="/agency-clients/:id/media-types/:configId"
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_AGENCY_CLIENT"]}
              component={AgencyClientFields}
              path="/agency-clients/:id/fields/:configId"
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_COPY"]}
              component={AgencyClientCopies}
              path="/agency-clients/:id/copies/:configId"
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_CLIENT_TARGET"]}
              component={AgencyClientTargets}
              path="/agency-clients/:id/targets/:configId"
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_FEE_CLIENT"]}
              path="/agency-clients/:id/costs/:configId"
            >
              <Surface className={styles.surface}>
                <Fees
                  queryKeyParam="agency-clients"
                  configScope="CLIENT_AGENCY_CONFIG"
                />
              </Surface>
            </AuthorizedRoute>
            {isTeamLead || isAgencyAdmin || isAgencyFinance ? (
              <Route path="/agency-clients/:id/exchange_rates/:configId">
                <AgencyClientExchangeRates />
              </Route>
            ) : (
              ""
            )}
            <Route path="/agency-clients/:id/commissions/:configId">
              <Commissions
                queryKeyParam="agency-clients"
                configScope="CLIENT_AGENCY_CONFIG"
              />
            </Route>
            <Route path="/agency-clients/:id/documents/:configId">
              <Surface className={cx(styles.surface, styles.documents)}>
                <AgencyClientDocuments />
              </Surface>
            </Route>
            {/* <AuthorizedRoute
              path="/agency-clients/:id/mba-configuration/:configId"
              allowed={["AUTH_VIEW_CLIENT"]}
              component={MbaConfiguration}
            /> */}
            <Route
              component={MbaConfiguration}
              path="/agency-clients/:id/mba-configuration/:configId"
            />
            <Route path="/agency-clients/:id/campaign-mba-configuration/:configId">
              <CampaignMbaConfig
                level="client-agency-configs"
                configScope="CLIENT_AGENCY"
                id={configId}
              />
            </Route>
            <Route component={AgencyClientProfile} />
          </Switch>
        </div>
      </Surface>
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
      <ImportModal
        title={t("import.settings.client.title")}
        description={t("import.settings.client.description")}
        submitLabel={t("import.settings.client.action")}
        clients={clients?.data}
        selectedClientId={selectedClientId}
        setSelectedClientId={setSelectedClientId}
        selectedAgencyId={selectedAgencyId}
        setSelectedAgencyId={setSelectedAgencyId}
        open={importModalOpen}
        onSubmit={handleSubmit}
        onClose={handleClose}
        isTeamLead={isTeamLead}
      />
    </Container>
  )
}

const styles = {
  row: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `,
  column: css`
    display: flex;
    flex-direction: column;
  `,
  actions: css`
    margin: auto 0 var(--spacing-xs);
  `,
  surface: css`
    padding: var(--spacing-xxl-2) 0 0;
  `,
  wrapper: css`
    overflow-x: clip;
    overflow-y: unset;
    padding: var(--spacing-xl) var(--spacing-m) var(--spacing-m);
  `,
  documents: css`
    display: flex;
    grid-column-gap: var(--spacing-xxl-2);
  `,
  tabsFlexContainer: css`
    min-width: max-content;
  `,
}
